import React, { useEffect, useRef, useState } from 'react';
import { Loader2, ZoomIn, ZoomOut, RotateCcw, Info, Plus } from 'lucide-react';
import { detectFileFormat } from '../lib/molecularParser';

interface ResidueInfo {
  chain: string;
  resNum: number;
  resName: string;
  index: number;
}

interface SequenceData {
  chain: string;
  sequence: string;
  residues: ResidueInfo[];
}

type MaskType = 'sequence' | 'structure' | 'sasa' | 'plddt';

interface MaskColors {
  sequence: string;
  structure: string;
  sasa: string;
  plddt: string;
}

const MASK_COLORS: MaskColors = {
  sequence: '#3B82F6', // blue
  structure: '#EF4444', // red
  sasa: '#10B981', // green
  plddt: '#FACC15', // yellow
};

interface Simple3DViewerProps {
  // Basic viewer props
  pdbContent?: string;
  pdbUrl?: string;
  height?: string;
  backgroundColor?: string;
  showControls?: boolean;
  highlightedResidues?: string[]; // Array of chain-position keys like "A-123"
  highlightColor?: 'orange' | 'red';

  // Multi-structure overlay props
  multiplePdbContents?: string[];
  structureColors?: string[];
  structureLabels?: string[];

  // Interactive masking features
  interactive?: boolean;
  showSequence?: boolean;
  maskTypes?: MaskType[];
  activeMaskTypes?: string[];
  maskedResidues?: { [key: string]: string[] };
  onMaskChange?: (maskType: string, residues: string[]) => void;
  onResidueClick?: (residue: ResidueInfo) => void;
  onResidueHover?: (residue: ResidueInfo | null) => void;
  readOnly?: boolean;
  sequences?: SequenceData[];

  // Manual mask input
  showManualMaskInput?: boolean;
  onManualMaskAdd?: (chain: string, range: string, maskType: string) => void;

  // Additional data indicators
  hasUploadedSasa?: boolean;
  hasUploadedSemantic?: boolean;
  hasUploadedOrthologous?: boolean;
  hasUploadedDomains?: boolean;
}

export function Molecule3DViewer({
  // Basic viewer props
  pdbContent,
  pdbUrl,
  height = 'h-96',
  backgroundColor = 'white',
  showControls = true,
  highlightedResidues = [],
  highlightColor = 'orange',

  // Multi-structure overlay props
  multiplePdbContents = [],
  structureColors = [],
  structureLabels = [],

  // Interactive masking features
  interactive = false,
  showSequence = false,
  maskTypes = [],
  activeMaskTypes = [],
  maskedResidues = {},
  onMaskChange,
  onResidueClick,
  onResidueHover,
  readOnly = false,
  sequences = [],

  // Manual mask input
  showManualMaskInput = false,
  onManualMaskAdd,

  // Additional data indicators
  hasUploadedSasa = false,
  hasUploadedSemantic = false,
  hasUploadedOrthologous = false,
  hasUploadedDomains = false
}: Simple3DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSpinning, setIsSpinning] = useState(true);
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  const spinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const spinPausedDueToZeroRef = useRef(false);
  const modelColorsRef = useRef<string[] | null>(null);
  const modelCountRef = useRef<number>(0);

  // Interactive masking state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartResidue, setDragStartResidue] = useState<ResidueInfo | null>(null);
  const [dragEndResidue, setDragEndResidue] = useState<ResidueInfo | null>(null);
  const [hoveredResidue, setHoveredResidue] = useState<ResidueInfo | null>(null);
  const [manualMaskChain, setManualMaskChain] = useState('');
  const [manualMaskRange, setManualMaskRange] = useState('');
  const [manualMaskError, setManualMaskError] = useState(false);

  // Sequence interaction refs
  const scrollContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollIntervalRef = useRef<number | null>(null);
  const initialMasksRef = useRef<Map<MaskType, Set<string>>>(new Map());

  // Stable keys to avoid re-initializing on every render due to [] defaults
  const multiKey = JSON.stringify(multiplePdbContents || []);
  const colorsKey = JSON.stringify(structureColors || []);
  const labelsKey = JSON.stringify(structureLabels || []);

  useEffect(() => {
    mountedRef.current = true;
    initializedRef.current = false; // Reset initialization flag when content changes

    if ((multiplePdbContents && multiplePdbContents.length > 0)) {
      // Multi-structure overlay mode
      initialize3DmolMultiple(multiplePdbContents, structureColors, structureLabels);
    } else if (pdbContent) {
      initialize3Dmol(pdbContent);
    } else if (pdbUrl) {
      fetchAndInitialize(pdbUrl);
    }

    return () => {
      mountedRef.current = false;

      // Clear any pending spin timeouts
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }

      // Stop spinning and cleanup viewer on unmount
      if (viewerRef.current) {
        try {
          viewerRef.current.spin(false); // Stop spinning
          viewerRef.current.clear();
          viewerRef.current = null;
        } catch (e) {
          console.warn('Error clearing 3Dmol viewer:', e);
        }
      }
      initializedRef.current = false;
    };
  }, [pdbContent, pdbUrl, multiKey, colorsKey, labelsKey]);

  // Keep viewer sized correctly when container size changes
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      if (!viewerRef.current || !initializedRef.current) return;
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      const { width, height } = rect;
      if (width > 0 && height > 0) {
        try {
          if (spinPausedDueToZeroRef.current && isSpinning) {
            // Resume spin if we previously paused due to zero sizing
            viewerRef.current.spin('vy', 1);
            spinPausedDueToZeroRef.current = false;
          }
          viewerRef.current.resize();
          viewerRef.current.render();
        } catch (e) {
          console.warn('3Dmol resize/render failed:', e);
        }
      } else {
        // Temporarily pause spinning to avoid WebGL errors when canvas is 0x0
        try {
          viewerRef.current.spin(false);
          spinPausedDueToZeroRef.current = true;
        } catch {}
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track last applied highlights to do incremental updates
  const lastHighlightsRef = useRef<Set<string>>(new Set());
  const rafRef = useRef<number | null>(null);

  // Update highlighting when highlightedResidues changes (throttled per frame)
  useEffect(() => {
    if (!viewerRef.current || !initializedRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!viewerRef.current || !initializedRef.current) return;
      if (multiplePdbContents.length > 0) {
        updateHighlightingMultiple();
      } else {
        updateHighlightingIncremental();
      }
    });
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [highlightedResidues, multiplePdbContents]);

  // Handle spinning animation
  useEffect(() => {
    console.log('🔍 Simple3DViewer: useEffect triggered', {
      isSpinning,
      loading,
      hasViewer: !!viewerRef.current,
      isInitialized: initializedRef.current,
      hasMounted: mountedRef.current
    });

    if (isSpinning && !loading) {
      // Simplified condition: !loading guarantees viewer is ready
      console.log('🌀 Simple3DViewer: Starting spinning animation (simplified condition)');
      
      // Add failsafe with small delay to ensure refs are set
      const startWithFailsafe = () => {
        if (!mountedRef.current) {
          console.log('🚫 Simple3DViewer: Component unmounted, canceling spin start');
          return;
        }

        if (viewerRef.current && initializedRef.current) {
          console.log('✅ Simple3DViewer: Failsafe confirmed - viewer ready');
          startSpinning();
        } else {
          console.warn('⚠️ Simple3DViewer: Failsafe delay - refs not ready, trying again');
          spinTimeoutRef.current = setTimeout(startWithFailsafe, 50);
        }
      };

      startWithFailsafe();
    } else if (!isSpinning) {
      console.log('🛑 Simple3DViewer: Stopping spinning animation');
      stopSpinning();
    } else {
      console.log('⚠️ Simple3DViewer: Spinning conditions not met', {
        isSpinning,
        loading
      });
    }
  }, [isSpinning, loading]);

  const startSpinning = () => {
    if (!viewerRef.current || !mountedRef.current) {
      console.warn('🚫 Simple3DViewer: Cannot start spinning - viewer or component not ready');
      return;
    }

    try {
      // Use 3Dmol.js built-in spinning around Z-axis (counterclockwise)
      console.log('🌀 Simple3DViewer: Starting 3Dmol built-in spinning');
      // Use string axis per 3Dmol API; pick visible speed
      const el = containerRef.current as HTMLDivElement | null;
      const w = el?.clientWidth || 0;
      const h = el?.clientHeight || 0;
      if (w === 0 || h === 0) {
        // Defer spin until container has size
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = setTimeout(startSpinning, 50);
        return;
      }
      viewerRef.current.spin('vy', 1);
      viewerRef.current.render();
    } catch (e) {
      console.warn('Error starting 3Dmol spinning:', e);
    }
  };

  const stopSpinning = () => {
    // Clear any pending spin timeout first
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }

    if (!viewerRef.current) return;

    try {
      console.log('🛑 Simple3DViewer: Stopping 3Dmol built-in spinning');
      viewerRef.current.spin(false); // Stop spinning
    } catch (e) {
      console.warn('Error stopping 3Dmol spinning:', e);
    }
  };

  // Incremental highlighter for single-model view
  const updateHighlightingIncremental = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const BASE_STYLE = { cartoon: { color: '#808080' }, stick: { radius: 0.15, color: '#808080' } };
    const colors = { orange: '#fb923c', red: '#EF4444' } as const;

    const next = new Set<string>(highlightedResidues || []);
    const prev = lastHighlightsRef.current;

    // Compute deltas
    const toRemove: string[] = [];
    prev.forEach(k => { if (!next.has(k)) toRemove.push(k); });
    const toAdd: string[] = [];
    next.forEach(k => { if (!prev.has(k)) toAdd.push(k); });

    // Revert removed residues to base style
    for (const residueKey of toRemove) {
      const [chain, resNumStr] = residueKey.split('-');
      const resi = parseInt(resNumStr);
      if (!chain || isNaN(resi)) continue;
      viewer.setStyle({ chain, resi }, BASE_STYLE);
    }

    // Apply highlight to added residues
    if (toAdd.length > 0) {
      const highlightColorHex = colors[highlightColor];
      for (const residueKey of toAdd) {
        const [chain, resNumStr] = residueKey.split('-');
        const resi = parseInt(resNumStr);
        if (!chain || isNaN(resi)) continue;
        viewer.setStyle(
          { chain, resi },
          { cartoon: { color: highlightColorHex }, stick: { radius: 0.15, color: highlightColorHex } }
        );
      }
    }

    // Update ref and render once
    lastHighlightsRef.current = next;
    viewer.render();
  };

  const fetchAndInitialize = async (url: string) => {
    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      // Check if it's a blob URL or regular URL
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch PDB file');
      }
      
      const content = await response.text();
      if (mountedRef.current) {
        await initialize3Dmol(content);
      }
    } catch (err) {
      console.error('Error fetching PDB:', err);
      if (mountedRef.current) {
        setError('Failed to load structure');
        setLoading(false);
      }
    }
  };

  const initialize3Dmol = async (content: string) => {
    if (!containerRef.current || !content || !mountedRef.current) return;

    // Prevent double initialization
    if (initializedRef.current) return;

    // Add a small stagger delay to prevent WebGL context conflicts when multiple viewers initialize simultaneously
    const randomDelay = Math.random() * 100; // 0-100ms random delay
    await new Promise(resolve => setTimeout(resolve, randomDelay));

    // Wait for container to have real dimensions to avoid zero-size framebuffer
    if (containerRef.current.clientWidth === 0 || containerRef.current.clientHeight === 0) {
      setTimeout(() => initialize3Dmol(content), 100);
      return;
    }

    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }

      // Check if 3Dmol is already loaded
      let $3Dmol = (window as any).$3Dmol;
      
      if (!$3Dmol) {
        // Load 3Dmol.js from CDN
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://3Dmol.org/build/3Dmol-min.js';
          script.onload = () => {
            $3Dmol = (window as any).$3Dmol;
            if ($3Dmol) {
              resolve();
            } else {
              reject(new Error('3Dmol failed to load'));
            }
          };
          script.onerror = () => reject(new Error('Failed to load 3Dmol script'));
          document.head.appendChild(script);
        });
      }

      if (!$3Dmol || !containerRef.current || !mountedRef.current) return;

      // Clean up existing viewer if any
      if (viewerRef.current) {
        try {
          viewerRef.current.clear();
        } catch (e) {
          console.warn('Error clearing existing viewer:', e);
        }
        viewerRef.current = null;
      }

      // Create a fresh container for 3Dmol
      if (viewerContainerRef.current) {
        viewerContainerRef.current.remove();
      }
      
      const viewerContainer = document.createElement('div');
      viewerContainer.style.width = '100%';
      viewerContainer.style.height = '100%';
      viewerContainerRef.current = viewerContainer;
      containerRef.current.appendChild(viewerContainer);

      // Create viewer
      const config = {
        backgroundColor: backgroundColor,
        antialias: true
      };
      
      const viewer = $3Dmol.createViewer(viewerContainer, config);

      // Detect format and add the model with appropriate format
      const format = detectFileFormat(content);
      const modelFormat = format === 'cif' ? 'cif' : 'pdb';
      viewer.addModel(content, modelFormat);
      
      // Store viewer reference first
      viewerRef.current = viewer;
      initializedRef.current = true;
      modelColorsRef.current = null; // Single-model mode
      modelCountRef.current = 1;

      // Center view
      viewer.zoomTo();

      // Reset highlight tracking and apply base styling + initial highlights
      lastHighlightsRef.current = new Set();
      const BASE_STYLE = { cartoon: { color: '#808080' }, stick: { radius: 0.15, color: '#808080' } };
      viewer.setStyle({}, BASE_STYLE);
      updateHighlightingIncremental();
      
      // Ensure spinning starts immediately for fresh viewers when enabled
      if (isSpinning) {
        startSpinning();
      }

      if (mountedRef.current) {
        console.log('🎯 Simple3DViewer: Viewer initialized, setting loading to false');
        setLoading(false);
        // Spinning will be handled by useEffect when loading becomes false
      }
    } catch (err) {
      console.error('Error initializing 3Dmol:', err);
      if (mountedRef.current) {
        setError('Failed to initialize viewer');
        setLoading(false);
      }
    }
  };

  const initialize3DmolMultiple = async (contents: string[], colors: string[] = [], labels: string[] = []) => {
    if (!containerRef.current || contents.length === 0 || !mountedRef.current) return;

    // Prevent double initialization
    if (initializedRef.current) return;

    // Add a small stagger delay to prevent WebGL context conflicts
    const randomDelay = Math.random() * 100;
    await new Promise(resolve => setTimeout(resolve, randomDelay));

    // Wait for container to have real dimensions
    if (containerRef.current.clientWidth === 0 || containerRef.current.clientHeight === 0) {
      setTimeout(() => initialize3DmolMultiple(contents, colors, labels), 100);
      return;
    }

    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }

      // Check if 3Dmol is already loaded
      let $3Dmol = (window as any).$3Dmol;

      if (!$3Dmol) {
        // Load 3Dmol.js from CDN
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://3Dmol.org/build/3Dmol-min.js';
          script.onload = () => {
            $3Dmol = (window as any).$3Dmol;
            if ($3Dmol) {
              resolve();
            } else {
              reject(new Error('3Dmol failed to load'));
            }
          };
          script.onerror = () => reject(new Error('Failed to load 3Dmol script'));
          document.head.appendChild(script);
        });
      }

      if (!$3Dmol || !containerRef.current || !mountedRef.current) return;

      // Clean up existing viewer if any
      if (viewerRef.current) {
        try {
          viewerRef.current.clear();
        } catch (e) {
          console.warn('Error clearing existing viewer:', e);
        }
        viewerRef.current = null;
      }

      // Create a fresh container for 3Dmol
      if (viewerContainerRef.current) {
        viewerContainerRef.current.remove();
      }

      const viewerContainer = document.createElement('div');
      viewerContainer.style.width = '100%';
      viewerContainer.style.height = '100%';
      viewerContainerRef.current = viewerContainer;
      containerRef.current.appendChild(viewerContainer);

      // Create viewer
      const config = {
        backgroundColor: backgroundColor,
        antialias: true
      };

      const viewer = $3Dmol.createViewer(viewerContainer, config);

      // Default colors if not provided
      const defaultColors = ['#808080', '#3B82F6']; // gray, blue
      const modelColors = colors.length > 0 ? colors : defaultColors;
      modelColorsRef.current = modelColors;
      modelCountRef.current = contents.length;

      // Add each structure as a separate model
      contents.forEach((content, index) => {
        const format = detectFileFormat(content);
        const modelFormat = format === 'cif' ? 'cif' : 'pdb';
        viewer.addModel(content, modelFormat);

        // Apply color to this model
        const color = modelColors[index] || modelColors[modelColors.length - 1] || '#808080';
        viewer.setStyle(
          { model: index },
          {
            cartoon: { color: color },
            stick: { radius: 0.15, color: color }
          }
        );
      });

      // Store viewer reference
      viewerRef.current = viewer;
      initializedRef.current = true;

      // Center view to show all models
      viewer.zoomTo();

      // Reset highlight tracking and apply initial highlighting (for both models)
      lastHighlightsRef.current = new Set();
      updateHighlightingMultiple();

      // Ensure spinning starts immediately for fresh viewers when enabled
      if (isSpinning) {
        startSpinning();
      }

      if (mountedRef.current) {
        console.log('🎯 Multi-structure viewer initialized, setting loading to false');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error initializing multi-structure 3Dmol:', err);
      if (mountedRef.current) {
        setError('Failed to initialize multi-structure viewer');
        setLoading(false);
      }
    }
  };

  const updateHighlightingMultiple = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const next = new Set<string>(highlightedResidues || []);
    const prev = lastHighlightsRef.current;

    // Compute deltas
    const toRemove: string[] = [];
    prev.forEach(k => { if (!next.has(k)) toRemove.push(k); });
    const toAdd: string[] = [];
    next.forEach(k => { if (!prev.has(k)) toAdd.push(k); });

    const highlightColorHex = highlightColor === 'red' ? '#EF4444' : '#fb923c';
    const modelColors = modelColorsRef.current || ['#808080', '#3B82F6'];
    const modelCount = modelCountRef.current || modelColors.length;

    // Revert removed residues back to per-model base colors
    for (const residueKey of toRemove) {
      const [chain, resNumStr] = residueKey.split('-');
      const resi = parseInt(resNumStr);
      if (!chain || isNaN(resi)) continue;
      for (let i = 0; i < modelCount; i++) {
        const baseColor = modelColors[i] || modelColors[modelColors.length - 1] || '#808080';
        viewer.setStyle(
          { chain, resi, model: i },
          { cartoon: { color: baseColor }, stick: { radius: 0.15, color: baseColor } }
        );
      }
    }

    // Apply highlight to added residues across all models
    for (const residueKey of toAdd) {
      const [chain, resNumStr] = residueKey.split('-');
      const resi = parseInt(resNumStr);
      if (!chain || isNaN(resi)) continue;
      viewer.setStyle(
        { chain, resi },
        { cartoon: { color: highlightColorHex }, stick: { radius: 0.15, color: highlightColorHex } }
      );
    }

    lastHighlightsRef.current = next;
    viewer.render();
  };

  const handleZoomIn = () => {
    if (viewerRef.current) {
      viewerRef.current.zoom(1.2);
    }
  };

  const handleZoomOut = () => {
    if (viewerRef.current) {
      viewerRef.current.zoom(0.8);
    }
  };

  const toggleSpin = () => {
    if (isSpinning) {
      stopSpinning();
      setIsSpinning(false);
    } else {
      startSpinning();
      setIsSpinning(true);
    }
  };

  // Interactive masking functions
  const applyMasksToResidue = (residue: ResidueInfo) => {
    if (!onMaskChange || !maskedResidues) return;

    const key = `${residue.chain}-${residue.resNum}`;
    const newMasks = { ...maskedResidues };

    // First, remove this residue from ALL mask types
    const allMaskTypes: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];
    allMaskTypes.forEach(maskType => {
      if (newMasks[maskType]) {
        newMasks[maskType] = newMasks[maskType].filter(r => r !== key);
      }
    });

    // Then add the residue ONLY for active mask types
    activeMaskTypes.forEach(maskType => {
      if (!newMasks[maskType]) {
        newMasks[maskType] = [];
      }
      if (!newMasks[maskType].includes(key)) {
        newMasks[maskType].push(key);
      }
    });

    // Notify parent of mask change
    Object.keys(newMasks).forEach(maskType => {
      if (onMaskChange) {
        onMaskChange(maskType, newMasks[maskType]);
      }
    });
  };

  const handleSequenceClick = (residue: ResidueInfo) => {
    if (onResidueClick) {
      onResidueClick(residue);
    } else {
      applyMasksToResidue(residue);
    }
  };

  const handleMouseDown = (residue: ResidueInfo) => {
    if (readOnly) return;

    setDragStartResidue(residue);
    setDragEndResidue(residue);

    // Save current mask state before drag starts
    if (maskedResidues) {
      const currentMasks = new Map<MaskType, Set<string>>();
      Object.entries(maskedResidues).forEach(([maskType, residues]) => {
        currentMasks.set(maskType as MaskType, new Set(residues));
      });
      initialMasksRef.current = currentMasks;
    }
  };

  const handleMouseEnter = (residue: ResidueInfo, event: React.MouseEvent) => {
    if (onResidueHover) {
      onResidueHover(residue);
    }
    setHoveredResidue(residue);

    if (readOnly || !dragStartResidue) return;

    // Start dragging if we have a start residue
    if (!isDragging) {
      setIsDragging(true);
    }

    if (residue.chain === dragStartResidue.chain) {
      setDragEndResidue(residue);

      // If we're dragging (moved to a different residue), select the range
      if (dragStartResidue.index !== residue.index) {
        const startIndex = dragStartResidue.index;
        const endIndex = residue.index;
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);

        // Apply masks to range
        if (onMaskChange && maskedResidues) {
          const newMasks = { ...maskedResidues };

          // Start with initial masks
          initialMasksRef.current.forEach((residues, maskType) => {
            newMasks[maskType] = Array.from(residues);
          });

          const chainSequence = sequences.find(seq => seq.chain === residue.chain);
          if (chainSequence) {
            chainSequence.residues.forEach(res => {
              const key = `${res.chain}-${res.resNum}`;

              // Apply masks to residues within the drag range
              if (res.index >= minIndex && res.index <= maxIndex) {
                // Remove from all mask types first
                const allMaskTypes: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];
                allMaskTypes.forEach(maskType => {
                  if (newMasks[maskType]) {
                    newMasks[maskType] = newMasks[maskType].filter(r => r !== key);
                  }
                });

                // Then add for active mask types
                activeMaskTypes.forEach(maskType => {
                  if (!newMasks[maskType]) {
                    newMasks[maskType] = [];
                  }
                  if (!newMasks[maskType].includes(key)) {
                    newMasks[maskType].push(key);
                  }
                });
              }
            });
          }

          // Notify parent of all mask changes
          Object.keys(newMasks).forEach(maskType => {
            onMaskChange(maskType, newMasks[maskType]);
          });
        }
      }

      // Auto-scroll logic
      const scrollContainer = scrollContainerRefs.current.get(residue.chain);
      if (scrollContainer && dragStartResidue) {
        const rect = scrollContainer.getBoundingClientRect();
        const mouseX = event.clientX;
        const scrollZoneSize = 60;
        const scrollSpeed = 10;

        // Clear any existing scroll interval
        if (scrollIntervalRef.current) {
          cancelAnimationFrame(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }

        // Check if mouse is in scroll zones
        if (mouseX < rect.left + scrollZoneSize) {
          // Scroll left
          const scrollAmount = Math.max(1, ((rect.left + scrollZoneSize - mouseX) / scrollZoneSize) * scrollSpeed);
          const scroll = () => {
            scrollContainer.scrollLeft -= scrollAmount;
            if (dragStartResidue) {
              scrollIntervalRef.current = requestAnimationFrame(scroll);
            }
          };
          scrollIntervalRef.current = requestAnimationFrame(scroll);
        } else if (mouseX > rect.right - scrollZoneSize) {
          // Scroll right
          const scrollAmount = Math.max(1, ((mouseX - (rect.right - scrollZoneSize)) / scrollZoneSize) * scrollSpeed);
          const scroll = () => {
            scrollContainer.scrollLeft += scrollAmount;
            if (dragStartResidue) {
              scrollIntervalRef.current = requestAnimationFrame(scroll);
            }
          };
          scrollIntervalRef.current = requestAnimationFrame(scroll);
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartResidue(null);
    setDragEndResidue(null);
    initialMasksRef.current = new Map();

    // Clear scroll interval
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // Add global mouse up listener to handle mouse up outside of residues
  useEffect(() => {
    if (!interactive) return;

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, interactive]);

  return (
    <div className="relative">
      {/* Sequence Panel */}
      {showSequence && sequences.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <style>{`
            .sequence-scroll {
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }
            .sequence-scroll::-webkit-scrollbar {
              height: 0px;
            }
          `}</style>

          {/* Mask type indicators */}
          {maskTypes.length > 0 && (
            <div className="mb-4 flex items-center gap-4">
              <span className="text-sm text-gray-600">Active Masks:</span>
              {maskTypes.map(maskType => (
                <div key={maskType} className="flex items-center gap-2">
                  <div
                    className="w-4 h-3 rounded"
                    style={{ backgroundColor: MASK_COLORS[maskType] }}
                  />
                  <span className="text-sm capitalize">{maskType}</span>
                  {(maskType === 'sasa' && hasUploadedSasa) ||
                   (maskType === 'structure' && hasUploadedSemantic) ||
                   (maskType === 'plddt' && hasUploadedOrthologous) ||
                   (maskType === 'sequence' && hasUploadedDomains) ? (
                    <span className="text-xs text-green-600">✓</span>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {/* Scrollable container for chains */}
          <div
            className="h-[110px] overflow-y-auto"
            onMouseLeave={() => {
              if (onResidueHover) {
                onResidueHover(null);
              }
              setHoveredResidue(null);
            }}
          >
            {sequences.map((seq) => (
              <div key={seq.chain} className="mb-3">
                <div className="text-base text-gray-500 mb-1">Chain {seq.chain}</div>
                <div className="relative overflow-hidden pb-1">
                  <div
                    className="sequence-scroll"
                    ref={(el) => {
                      if (el) scrollContainerRefs.current.set(seq.chain, el);
                    }}
                  >
                    <div className="font-mono text-lg select-none inline-block pr-4 sequence-container">
                      {seq.residues.map((residue, idx) => {
                        const key = `${residue.chain}-${residue.resNum}`;
                        const activeMasks: MaskType[] = [];
                        const maskOrder: MaskType[] = ['sequence', 'structure', 'sasa', 'plddt'];

                        maskOrder.forEach(maskType => {
                          if (maskedResidues[maskType]?.includes(key)) {
                            activeMasks.push(maskType);
                          }
                        });

                        return (
                          <span key={key}>
                            <span
                              className={`inline-block px-1.5 pb-0 select-none relative mask-indicator ${
                                readOnly ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (!readOnly && interactive) {
                                  handleMouseDown(residue);
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (interactive) {
                                  handleMouseEnter(residue, e);
                                }
                              }}
                              onMouseUp={interactive ? handleMouseUp : undefined}
                              onClick={() => {
                                if (!isDragging && !readOnly && interactive) {
                                  handleSequenceClick(residue);
                                }
                              }}
                              title={`${residue.resName} ${residue.resNum}`}
                            >
                              {residue.resName}
                              {/* Render underlines as absolute positioned divs */}
                              {activeMasks.map((maskType) => {
                                const positionMap: Record<MaskType, number> = {
                                  'sequence': 0,   // Blue at top
                                  'structure': 3,  // Red 3px below
                                  'sasa': 6,       // Green 6px below
                                  'plddt': 9       // Yellow 9px below (bottom)
                                };

                                return (
                                  <div
                                    key={maskType}
                                    style={{
                                      position: 'absolute',
                                      top: `calc(100% - 4px + ${positionMap[maskType]}px)`,
                                      left: '0',
                                      right: '0',
                                      height: '3px',
                                      backgroundColor: MASK_COLORS[maskType],
                                    }}
                                  />
                                );
                              })}
                            </span>
                            {(idx + 1) % 10 === 0 && (
                              <span className="text-base text-gray-400 ml-0.5 mr-1">{residue.resNum}</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Mask Input Controls */}
      {showManualMaskInput && interactive && !readOnly && (
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Add Manual Mask:</span>
            <input
              type="text"
              placeholder="Chain"
              value={manualMaskChain}
              onChange={(e) => {
                setManualMaskChain(e.target.value.toUpperCase().slice(0, 1));
                setManualMaskError(false);
              }}
              className={`w-16 px-2 py-1 text-sm border rounded ${
                manualMaskError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              maxLength={1}
            />
            <input
              type="text"
              placeholder="Range (e.g., 123 or 123-456)"
              value={manualMaskRange}
              onChange={(e) => {
                setManualMaskRange(e.target.value);
                setManualMaskError(false);
              }}
              className={`w-40 px-2 py-1 text-sm border rounded ${
                manualMaskError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
            />
            <button
              onClick={() => {
                if (!manualMaskChain || !manualMaskRange) {
                  setManualMaskError(true);
                  return;
                }

                // Validate and apply manual mask for each active mask type
                activeMaskTypes.forEach(maskType => {
                  if (onManualMaskAdd) {
                    onManualMaskAdd(manualMaskChain, manualMaskRange, maskType);
                  }
                });

                // Clear inputs
                setManualMaskChain('');
                setManualMaskRange('');
                setManualMaskError(false);
              }}
              disabled={!manualMaskChain || !manualMaskRange || activeMaskTypes.length === 0}
              className="px-3 py-1 text-sm bg-gray-900 text-white rounded hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4 inline mr-1" />
              Add
            </button>
            {manualMaskError && (
              <span className="text-xs text-red-600">Please fill in both fields</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Range examples: "123" for single residue, "123-456" for range
          </p>
        </div>
      )}

      {/* 3D Viewer Container */}
      <div
        ref={containerRef}
        className={`${height} w-full rounded-lg overflow-hidden`}
        style={{
          backgroundColor: backgroundColor === 'dark' ? '#1f2937' : backgroundColor
        }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Loading structure...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {showControls && !loading && !error && (
        <div className="absolute top-2 right-2 bg-white shadow-lg flex">
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-gray-100 transition-colors border-r border-gray-200"
            title="Zoom In"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-gray-100 transition-colors border-r border-gray-200"
            title="Zoom Out"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <button
            onClick={toggleSpin}
            className={`p-2 hover:bg-gray-100 transition-colors ${isSpinning ? 'bg-blue-100 text-blue-600' : ''}`}
            title={isSpinning ? "Stop Spinning" : "Start Spinning"}
          >
            <RotateCcw className={`h-5 w-5 ${isSpinning ? 'animate-spin' : ''}`} style={{
              animation: isSpinning ? 'spin 2s linear infinite reverse' : 'none'
            }} />
          </button>
        </div>
      )}
    </div>
  );
}
